import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { Card } from 'react-daisyui'
import { API } from '../../utils/api'
import { Api } from '../../utils/apiServer'
import { auth, session } from '../../utils/AuthSession'
import { getGuestVisitorId, getMemberActivityKey, shouldRecordActivityVisit } from '../../utils/activityVisitGuard'
import { clearPendingCartAdd, readPendingCartAdd, readProductCatalogContext, savePendingCartAdd, saveProductCatalogContext } from '../../utils/productCatalogContext'
import { useLocation, useNavigate, useNavigationType } from 'react-router-dom'
import { ToastContainer, toast } from 'react-toastify'
import { AiFillStar, AiOutlineClose, AiOutlineDelete, AiOutlineGift, AiOutlineMinus, AiOutlinePlus, AiOutlineSearch, AiOutlineTag } from 'react-icons/ai'
import DefaultProduct from '../../assets/default-product.jpg'
import { ThemeContext } from '../../context/ThemeContext'
import { StoreContext } from '../../context/StoreContext'
const BEST_SELLER_TAB_ID = '__BEST_SELLER__'
const LEGACY_ALL_CATEGORY_ID = '__ALL_CATEGORY__'
const LOW_STOCK_THRESHOLD = 5
const SEARCH_MIN_LENGTH = 2
const SEARCH_DEBOUNCE_MS = 400
const SUGGESTION_LIMIT = 6
const PRODUCT_ROWS = 15
const CATALOG_REQUEST_TIMEOUT = 10000

const toNumber = (value) => {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : 0
}

const mergeProducts = (currentProducts, incomingProducts) => {
    const existingIds = new Set(currentProducts.map((product) => product.menuId))
    const incomingById = new Map(incomingProducts.map((product) => [product.menuId, product]))
    return [
        ...currentProducts.map((product) => incomingById.get(product.menuId) || product),
        ...incomingProducts.filter((product) => !existingIds.has(product.menuId)),
    ]
}

const sortProductsByStock = (products) => (
    [...products].sort((firstProduct, secondProduct) => secondProduct.stock - firstProduct.stock)
)

const isResponseSuccess = (response) => {
    if (response?.status === true) return true
    if (typeof response?.success === 'string' && response.success.length > 0) return true
    return false
}

const pickMenuForItem = (menus) => {
    if (!Array.isArray(menus) || menus.length === 0) return null

    const withStock = menus.find((menu) => toNumber(menu?.menu_current_quantity) > 0)
    return withStock || menus[0]
}

const pickFirstFiniteNumber = (...values) => {
    for (const value of values) {
        if (value === null || value === undefined) continue
        if (typeof value === 'string' && value.trim() === '') continue

        const parsed = Number(value)
        if (Number.isFinite(parsed)) return parsed
    }

    return null
}

const resolveReviewSummary = (item, menu) => {
    const reviewSummary =
        item?.review_summary && typeof item.review_summary === 'object'
            ? item.review_summary
            : null

    const averageRating =
        pickFirstFiniteNumber(
            reviewSummary?.average,
            item?.review_average,
            item?.item_review_average,
            item?.average_rating,
            item?.rating,
            item?.item_rating,
            menu?.review_average,
            menu?.menu_review_average,
            menu?.rating,
            menu?.menu_rating
        ) ?? 0

    const totalReviews =
        pickFirstFiniteNumber(
            reviewSummary?.total,
            item?.review_count,
            item?.item_review_count,
            item?.total_review,
            item?.total_reviews,
            menu?.review_count,
            menu?.menu_review_count,
            menu?.total_review
        ) ?? 0

    return {
        rating: Math.max(0, Math.min(5, averageRating)),
        reviewCount: Math.max(0, Math.floor(totalReviews)),
    }
}

const mapItemToCard = (item) => {
    const menu = pickMenuForItem(item?.menu)
    const firstImage = Array.isArray(item?.item_image) ? item.item_image[0] : item?.item_image

    const regularPrice = toNumber(item?.menu_regular_price ?? menu?.menu_regular_price)
    const discountPrice = toNumber(item?.menu_discount_marketplace_price ?? menu?.menu_discount_marketplace_price)
    const hasMarketplaceDiscount = Boolean(item?.has_marketplace_discount) && discountPrice > 0 && discountPrice < regularPrice
    const currentPrice = toNumber(item?.current_price) || (hasMarketplaceDiscount ? discountPrice : regularPrice)
    const stock = toNumber(item?.menu_current_quantity ?? menu?.menu_current_quantity)
    const { rating, reviewCount } = resolveReviewSummary(item, menu)

    return {
        menuId: item?.menu_id ?? menu?.menu_id,
        itemId: item?.item_id,
        itemSlug: item?.item_slug || item?.slug || item?.menu_slug || '',
        itemName: item?.item_name || 'Produk tanpa nama',
        imageUrl: firstImage || DefaultProduct,
        currentPrice,
		regularPrice,
		discountMarketplacePrice: hasMarketplaceDiscount ? discountPrice : null,
		hasMarketplaceDiscount,
        stock,
        rating,
        reviewCount,
        isOutOfStock: stock <= 0,
    }
}

const mapBestSellerToCard = (product) => {
    const regularPrice = toNumber(product?.menu_regular_price)
    const discountPrice = toNumber(product?.menu_discount_marketplace_price)
    const hasMarketplaceDiscount = Boolean(product?.has_marketplace_discount) && discountPrice > 0 && discountPrice < regularPrice
    const { rating, reviewCount } = resolveReviewSummary(product, null)
    const stock = toNumber(product?.stock)

    return {
        menuId: product?.menu_id,
        itemId: product?.item_id,
        itemSlug: product?.menu_slug || product?.item_slug || '',
        itemName: product?.item_name || 'Produk tanpa nama',
        imageUrl: product?.image_url || DefaultProduct,
        currentPrice: toNumber(product?.current_price) || (hasMarketplaceDiscount ? discountPrice : regularPrice),
        regularPrice,
        discountMarketplacePrice: hasMarketplaceDiscount ? discountPrice : null,
        hasMarketplaceDiscount,
        stock,
        rating,
        reviewCount,
        soldQuantity: toNumber(product?.sold_quantity),
        rank: toNumber(product?.rank),
        isOutOfStock: stock <= 0,
    }
}

const filterBestSellersByKeyword = (products, keyword) => {
    const normalizedKeyword = `${keyword || ''}`.trim().toLowerCase()
    if (!normalizedKeyword) return products
    return products.filter((product) => `${product?.itemName || ''}`.toLowerCase().includes(normalizedKeyword))
}

const mapCategoryToTab = (category) => ({
    id: category?.category_display_id,
    name: category?.category_display_name || 'Tanpa kategori',
})

const CAROUSEL_INTERVAL_MS = 4000

function PromoBannerCarousel({ banners, onBannerClick }) {
    const [activeIndex, setActiveIndex] = useState(0)
    const [isAnimating, setIsAnimating] = useState(false)
    const isPausedRef = useRef(false)
    const intervalRef = useRef(null)
    const count = banners.length

    const goTo = useCallback((index) => {
        if (isAnimating || count <= 1) return
        setIsAnimating(true)
        setActiveIndex((index + count) % count)
        setTimeout(() => setIsAnimating(false), 350)
    }, [isAnimating, count])

    const goNext = useCallback(() => goTo(activeIndex + 1), [goTo, activeIndex])
    const goPrev = useCallback(() => goTo(activeIndex - 1), [goTo, activeIndex])

    useEffect(() => {
        if (count <= 1) return
        intervalRef.current = setInterval(() => {
            if (!isPausedRef.current) {
                setActiveIndex((prev) => (prev + 1) % count)
            }
        }, CAROUSEL_INTERVAL_MS)
        return () => clearInterval(intervalRef.current)
    }, [count])

    if (count === 0) return null

    const banner = banners[activeIndex]
    const isClickable = banner.target_type !== 'none'

    return (
        <div
            className="relative mb-3 overflow-hidden rounded-xl shadow-sm"
            onMouseEnter={() => { isPausedRef.current = true }}
            onMouseLeave={() => { isPausedRef.current = false }}
            onTouchStart={() => { isPausedRef.current = true }}
            onTouchEnd={() => { isPausedRef.current = false }}
        >
            {/* Slide utama */}
            <button
                type="button"
                onClick={() => onBannerClick(banner)}
                disabled={!isClickable}
                className={`block w-full text-left ${isClickable ? 'cursor-pointer' : 'cursor-default'}`}
                style={{ transition: 'opacity 0.35s ease' }}
            >
                <img
                    key={activeIndex}
                    src={banner.image_url}
                    alt={banner.title}
                    className="aspect-[16/7] w-full object-cover"
                    style={{
                        animation: count > 1 ? 'bannerFadeIn 0.35s ease' : undefined,
                    }}
                    onError={(e) => { e.currentTarget.style.display = 'none' }}
                />
            </button>

            {/* Tombol prev / next — hanya tampil jika ada lebih dari 1 banner */}
            {count > 1 && (
                <>
                    <button
                        type="button"
                        onClick={goPrev}
                        aria-label="Banner sebelumnya"
                        className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-black/30 p-1.5 text-white backdrop-blur-sm transition hover:bg-black/50 focus:outline-none"
                    >
                        <svg viewBox="0 0 24 24" className="h-4 w-4 fill-none stroke-current stroke-2"><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" /></svg>
                    </button>
                    <button
                        type="button"
                        onClick={goNext}
                        aria-label="Banner berikutnya"
                        className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-black/30 p-1.5 text-white backdrop-blur-sm transition hover:bg-black/50 focus:outline-none"
                    >
                        <svg viewBox="0 0 24 24" className="h-4 w-4 fill-none stroke-current stroke-2"><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
                    </button>
                </>
            )}

            {/* Dot indicator */}
            {count > 1 && (
                <div className="absolute bottom-2 left-0 right-0 flex justify-center gap-1.5">
                    {banners.map((_, i) => (
                        <button
                            key={i}
                            type="button"
                            aria-label={`Banner ${i + 1}`}
                            onClick={() => goTo(i)}
                            className={`h-1.5 rounded-full transition-all duration-300 ${i === activeIndex ? 'w-5 bg-white' : 'w-1.5 bg-white/50'}`}
                        />
                    ))}
                </div>
            )}
        </div>
    )
}

function Product() {
    const { primaryColor, primaryColorHover } = useContext(ThemeContext)
    const { stores, activeStore, activeStoreSlug, loading: storeLoading, error: storeError, selectStore } = useContext(StoreContext)
    const location = useLocation()
    const navigationType = useNavigationType()
    const navigate = useNavigate()

    const handleCheckout = async () => {
        try {
            const profileResponse = await auth()
            const firstName = `${profileResponse?.data?.firstname || ''}`.trim()
            if (!firstName) {
                toast.error('Nama harus diisi')
                setTimeout(() => {
                    navigate('/profile')
                }, 700)
                return
            }

            navigate('/order', {
                state: {
                    selected_cart_ids: cartState.items.map((item) => item.cart_id),
                    store_slug: activeStoreSlug
                }
            })
        } catch (error) {
            toast.error(error?.message || 'Gagal memeriksa profil')
        }
    }
    const initialCatalogContextRef = useRef(
        location.state?.productCatalogContext ||
        (navigationType === 'POP' ? readProductCatalogContext() : null)
    )
    const initialCatalogContext = initialCatalogContextRef.current
    const initialCategoryId = initialCatalogContext?.activeCategoryId === LEGACY_ALL_CATEGORY_ID
        ? BEST_SELLER_TAB_ID
        : (initialCatalogContext?.activeCategoryId || BEST_SELLER_TAB_ID)
    const hasRestoredCatalog = initialCatalogContext?.activeCategoryId !== LEGACY_ALL_CATEGORY_ID &&
        Array.isArray(initialCatalogContext?.products) && initialCatalogContext?.storeSlug === activeStoreSlug

    const [products, setProducts] = useState(() => hasRestoredCatalog ? initialCatalogContext.products : [])
    const [categories, setCategories] = useState([])
    const [activeCategoryId, setActiveCategoryId] = useState(initialCategoryId)
    const [loading, setLoading] = useState(!hasRestoredCatalog)
    const [searchInput, setSearchInput] = useState(initialCatalogContext?.searchInput || '')
    const [searchKeyword, setSearchKeyword] = useState(initialCatalogContext?.searchKeyword || '')
    const [suggestionsOpen, setSuggestionsOpen] = useState(false)
    const [page, setPage] = useState(initialCatalogContext?.page || 1)
    const [lastPage, setLastPage] = useState(initialCatalogContext?.lastPage || 1)
    const [totalProducts, setTotalProducts] = useState(initialCatalogContext?.totalProducts || 0)
    const [isPageChanging, setIsPageChanging] = useState(false)
    const [loadingMore, setLoadingMore] = useState(false)
    const [memberPoints, setMemberPoints] = useState(0)
    const [storeModalOpen, setStoreModalOpen] = useState(false)
    const [storeChanging, setStoreChanging] = useState(false)
    const [navigatingToProduct, setNavigatingToProduct] = useState(false)
    const [promoBanners, setPromoBanners] = useState([])
    const [bestSellerProducts, setBestSellerProducts] = useState(() => (
        hasRestoredCatalog && initialCategoryId === BEST_SELLER_TAB_ID ? initialCatalogContext.products : []
    ))
    const [cartState, setCartState] = useState({ items: [], summary: { item_count: 0, subtotal: 0 } })
    const [cartLoading, setCartLoading] = useState(false)
    const [cartMutating, setCartMutating] = useState({})
    const isBestSellerTab = activeCategoryId === BEST_SELLER_TAB_ID
    const selectedCategoryName = isBestSellerTab
        ? ''
        : (categories.find((category) => category.id === activeCategoryId)?.name || '')

    const searchContainerRef = useRef(null)
    const productGridRef = useRef(null)
    const hasLoadedProductsRef = useRef(hasRestoredCatalog)
    const skipInitialProductFetchRef = useRef(hasRestoredCatalog && initialCategoryId !== BEST_SELLER_TAB_ID)
    const skipInitialSearchSyncRef = useRef(Boolean(initialCatalogContext))

    const catalogContext = useCallback(() => {
        const mainScrollContainer = document.getElementById('main-app-scroll-container')
        return { originPath: '/product', activeCategoryId, searchInput, searchKeyword, page, lastPage, totalProducts, products, storeSlug: activeStoreSlug, scrollTop: mainScrollContainer?.scrollTop || 0 }
    }, [activeCategoryId, activeStoreSlug, lastPage, page, products, searchInput, searchKeyword, totalProducts])

    const applyCartResponse = useCallback((response) => {
        const data = response?.data || {}
        setCartState({ items: Array.isArray(data.items) ? data.items : [], summary: data.summary || { item_count: 0, subtotal: 0 }, store: data.store || null })
    }, [])

    const loadCart = useCallback(async () => {
        if (!activeStoreSlug || !session()) { setCartState({ items: [], summary: { item_count: 0, subtotal: 0 } }); return }
        setCartLoading(true)
        try {
            const response = await Api({ access: 'auth', method: API.CART.type, url: API.CART.url, reqBody: { store_slug: activeStoreSlug } })
            if (response?.status) applyCartResponse(response)
            else setCartState({ items: [], summary: { item_count: 0, subtotal: 0 } })
        } finally { setCartLoading(false) }
    }, [activeStoreSlug, applyCartResponse])

    useEffect(() => { loadCart() }, [loadCart])

    const cartByMenuId = useMemo(() => new Map(cartState.items.map((item) => [`${item.menu_id}`, item])), [cartState.items])

    const mutateCart = useCallback(async ({ product, cart, quantity, remove = false }) => {
        const key = `${product?.menuId || cart?.menu_id}`
        if (cartMutating[key]) return
        if (!session()) {
            const context = catalogContext()
            saveProductCatalogContext(context)
            savePendingCartAdd({ menuId: product?.menuId, storeSlug: activeStoreSlug, returnPath: '/product' })
            navigate('/auth/login', { state: { productCatalogContext: context, returnTo: '/product' } })
            return
        }
        setCartMutating((current) => ({ ...current, [key]: true }))
        try {
            const response = await Api({
                access: 'auth',
                method: remove ? API.DELETE_CART.type : (cart ? API.EDIT_CART.type : API.ADD_CART.type),
                url: remove ? API.DELETE_CART.url : (cart ? API.EDIT_CART.url : API.ADD_CART.url),
                reqBody: remove ? { cart_id: cart.cart_id } : (cart ? { cart_id: cart.cart_id, quantity } : { menu_id: product.menuId, store_slug: activeStoreSlug, quantity: 1 })
            })
            if (!response?.status) { toast.error(response?.message || 'Gagal memperbarui keranjang'); return }
            applyCartResponse(response)
        } catch (error) { toast.error('Gagal memperbarui keranjang') }
        finally { setCartMutating((current) => { const next = { ...current }; delete next[key]; return next }) }
    }, [activeStoreSlug, applyCartResponse, cartMutating, catalogContext, navigate])

    useEffect(() => {
        const intent = readPendingCartAdd()
        if (!intent || !session()) return
        if (intent.storeSlug !== activeStoreSlug) { selectStore(intent.storeSlug); return }
        clearPendingCartAdd()
        const product = products.find((item) => `${item.menuId}` === `${intent.menuId}`)
        if (product) mutateCart({ product })
    }, [activeStoreSlug, mutateCart, products, selectStore])

    useEffect(() => {
        const currentSession = session()

        const recordProductPageVisit = async () => {
            const isLoggedIn = Boolean(currentSession)
            const visitorId = isLoggedIn ? null : getGuestVisitorId()
            const identityKey = isLoggedIn
                ? `member:${getMemberActivityKey(currentSession)}`
                : `guest:${visitorId}`

            if (!shouldRecordActivityVisit(`product-page:${identityKey}`)) return

            await Api({
                access: isLoggedIn ? 'auth' : 'public',
                method: isLoggedIn ? API.MEMBER_PRODUCT_PAGE_VISIT.type : API.PUBLIC_PRODUCT_PAGE_VISIT.type,
                url: isLoggedIn ? API.MEMBER_PRODUCT_PAGE_VISIT.url : API.PUBLIC_PRODUCT_PAGE_VISIT.url,
                reqBody: {
                    page: '/product',
                    visitor_id: visitorId,
                },
            })
        }

        recordProductPageVisit()
    }, [])

    useEffect(() => {
        let current = true
        Api({ access: 'public', method: API.PUBLIC_PROMO_BANNERS.type, url: API.PUBLIC_PROMO_BANNERS.url, reqBody: {} })
            .then((response) => { if (current && response?.status) setPromoBanners(Array.isArray(response.data) ? response.data : []) })
            .catch(() => { if (current) setPromoBanners([]) })
        return () => { current = false }
    }, [])

    const handlePromoClick = (banner) => {
        if (banner.target_type === 'product' && banner.target_item_slug) { handleProductClick({ menuId: banner.target_menu_id, itemSlug: banner.target_item_slug }); return }
        if (banner.target_type === 'internal_url' && banner.target_url) { navigate(banner.target_url); return }
        if (banner.target_type === 'external_url' && banner.target_url) window.open(banner.target_url, '_blank', 'noopener,noreferrer')
    }

    useEffect(() => {
        if (!session()) return

        let isCurrent = true
        const loadMemberPoints = async () => {
            try {
                const response = await Api({
                    access: 'auth',
                    method: API.PROFILE.type,
                    url: API.PROFILE.url,
                    reqBody: {},
                })
                const pointBalance = toNumber(response?.data?.point?.point ?? response?.data?.point)
                if (isCurrent) setMemberPoints(Math.max(0, pointBalance))
            } catch (error) {
                if (isCurrent) setMemberPoints(0)
            }
        }

        loadMemberPoints()
        return () => { isCurrent = false }
    }, [])

    useEffect(() => {
        if (skipInitialSearchSyncRef.current) {
            skipInitialSearchSyncRef.current = false
            return
        }

        const normalizedKeyword = searchInput.trim()
        const timeout = setTimeout(() => {
            setPage(1)
            setSearchKeyword(normalizedKeyword.length >= SEARCH_MIN_LENGTH ? normalizedKeyword : '')
        }, SEARCH_DEBOUNCE_MS)

        return () => clearTimeout(timeout)
    }, [searchInput])

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (!searchContainerRef.current) return
            if (!searchContainerRef.current.contains(event.target)) {
                setSuggestionsOpen(false)
            }
        }

        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [])

    useEffect(() => {
        let isCurrent = true

        const loadCategories = async () => {
            if (!activeStoreSlug) {
                if (!storeLoading && isCurrent) {
                    setProducts([])
                    setLastPage(1)
                    setTotalProducts(0)
                    setLoading(false)
                    setIsPageChanging(false)
                    setLoadingMore(false)
                }
                return
            }
            const params = {
                access: 'public',
                method: API.PUBLIC_CATEGORY.type,
                url: API.PUBLIC_CATEGORY.url,
                reqBody: { store_slug: activeStoreSlug },
                timeout: CATALOG_REQUEST_TIMEOUT,
            }

            try {
                const response = await Api(params)
                if (!isResponseSuccess(response)) {
                    if (isCurrent) setCategories([])
                    return
                }

                const categoryData = Array.isArray(response?.data) ? response.data : []
                const validCategories = categoryData
                    .filter((category) => category?.category_display_id !== undefined && category?.category_display_id !== null)
                    .map(mapCategoryToTab)

                if (isCurrent) setCategories(validCategories)
            } catch (error) {
                if (isCurrent) setCategories([])
            }
        }

        loadCategories()
        return () => { isCurrent = false }
    }, [activeStoreSlug])

    useEffect(() => {
        let isCurrent = true

        const loadBestSellers = async () => {
            if (!activeStoreSlug || !isBestSellerTab) return
            setLoading(true)
            setIsPageChanging(false)
            setLoadingMore(false)
            setBestSellerProducts([])
            setProducts([])
            setPage(1)
            setLastPage(1)
            setTotalProducts(0)

            try {
                const response = await Api({
                    access: 'public',
                    method: API.PUBLIC_PRODUCT_BEST_SELLERS.type,
                    url: API.PUBLIC_PRODUCT_BEST_SELLERS.url,
                    reqBody: { store_slug: activeStoreSlug },
                    timeout: CATALOG_REQUEST_TIMEOUT,
                })
                if (!isResponseSuccess(response)) {
                    if (isCurrent) toast.error(response?.message || 'Gagal memuat produk terlaris')
                    return
                }

                const mapped = (Array.isArray(response?.data?.products) ? response.data.products : [])
                    .map(mapBestSellerToCard)
                    .filter((product) => product.menuId && product.itemSlug)

                if (!isCurrent) return
                setBestSellerProducts(mapped)
                hasLoadedProductsRef.current = true
            } catch (error) {
                if (isCurrent) {
                    setBestSellerProducts([])
                    setProducts([])
                    toast.error('Terjadi kesalahan saat mengambil produk terlaris')
                }
            } finally {
                if (isCurrent) setLoading(false)
            }
        }

        loadBestSellers()
        return () => { isCurrent = false }
    }, [activeStoreSlug, isBestSellerTab])

    useEffect(() => {
        if (!isBestSellerTab) return
        const filteredProducts = filterBestSellersByKeyword(bestSellerProducts, searchKeyword)
        setProducts(filteredProducts)
        setPage(1)
        setLastPage(1)
        setTotalProducts(filteredProducts.length)
    }, [bestSellerProducts, isBestSellerTab, searchKeyword])

    useEffect(() => {
        if (isBestSellerTab) return undefined
        if (skipInitialProductFetchRef.current) {
            skipInitialProductFetchRef.current = false
            return
        }

        let isCurrent = true

        const loadProducts = async () => {
            if (!activeStoreSlug) return
            const isAppending = page > 1

            if (isAppending) {
                setLoadingMore(true)
            } else if (hasLoadedProductsRef.current) {
                setIsPageChanging(true)
            } else {
                setLoading(true)
            }

            const reqBody = {
                store_slug: activeStoreSlug,
                page,
                rows: PRODUCT_ROWS,
            }

            reqBody.category_display_id = activeCategoryId
            if (selectedCategoryName) reqBody.category_display_name = selectedCategoryName

            if (searchKeyword) {
                reqBody.keyword = searchKeyword
            }

            const params = {
                access: 'public',
                method: API.PUBLIC_PRODUCT.type,
                url: API.PUBLIC_PRODUCT.url,
                reqBody,
                timeout: CATALOG_REQUEST_TIMEOUT,
            }

            try {
                const response = await Api(params)
                const items = Array.isArray(response?.data) ? response.data : []

                if (!isResponseSuccess(response)) {
                    if (!isAppending) setProducts([])
                    toast.error(response?.message || 'Gagal memuat produk')
                    return
                }

                const mappedProducts = items.map((item) => mapItemToCard(item))
                if (!isCurrent) return

                setProducts((currentProducts) => sortProductsByStock(
                    isAppending ? mergeProducts(currentProducts, mappedProducts) : mappedProducts
                ))
                setLastPage(Math.max(1, Number(response?.last_page || response?.lastPage) || 1))
                setTotalProducts(Math.max(0, Number(response?.total) || 0))
                hasLoadedProductsRef.current = true
                setLoading(false)
            } catch (error) {
                if (isCurrent) {
                    if (!isAppending) {
                        setProducts([])
                        setLastPage(1)
                        setTotalProducts(0)
                    }
                }
                toast.error('Terjadi kesalahan saat mengambil produk')
            } finally {
                if (isCurrent) {
                    setLoading(false)
                    setIsPageChanging(false)
                    setLoadingMore(false)
                }
            }
        }

        loadProducts()

        return () => {
            isCurrent = false
        }
    }, [activeCategoryId, isBestSellerTab, selectedCategoryName, page, searchKeyword, activeStoreSlug, storeLoading])

    useEffect(() => {
        if (!activeStoreSlug) return
        setProducts([])
        setCategories([])
        setBestSellerProducts([])
        setActiveCategoryId(BEST_SELLER_TAB_ID)
        setPage(1)
        setLastPage(1)
        setTotalProducts(0)
        setSuggestionsOpen(false)
    }, [activeStoreSlug])

    useEffect(() => {
        if (!initialCatalogContext) return

        const frame = window.requestAnimationFrame(() => {
            const mainScrollContainer = document.getElementById('main-app-scroll-container')
            mainScrollContainer?.scrollTo({
                top: Number(initialCatalogContext.scrollTop) || 0,
                behavior: 'auto',
            })
        })

        return () => window.cancelAnimationFrame(frame)
    }, [initialCatalogContext])

    const categoryTabs = useMemo(() => {
        return [
            { id: BEST_SELLER_TAB_ID, name: 'Produk Terlaris' },
            ...categories.filter((category) => category?.id !== BEST_SELLER_TAB_ID && category?.id !== LEGACY_ALL_CATEGORY_ID),
        ]
    }, [categories])
    const activeCategoryName = categoryTabs.find((category) => category.id === activeCategoryId)?.name || 'Produk Terlaris'

    const normalizedSearchInput = searchInput.trim()
    const searchReady = normalizedSearchInput.length >= SEARCH_MIN_LENGTH
    const searchActive = searchKeyword.length >= SEARCH_MIN_LENGTH
    const waitingForSearch = searchReady && normalizedSearchInput !== searchKeyword
    const showSuggestions = suggestionsOpen && searchReady
    const productSuggestions = useMemo(() => {
        if (!showSuggestions || waitingForSearch) return []
        return products.slice(0, SUGGESTION_LIMIT)
    }, [products, showSuggestions, waitingForSearch])

    const handleCategoryClick = (categoryId) => {
        if (categoryId === undefined || categoryId === null) return

        setActiveCategoryId(categoryId)
        setPage(1)
        setTotalProducts(0)
        setSuggestionsOpen(false)
    }

    const handleProductClick = (product) => {
        if (navigatingToProduct) return
        if (!product?.itemSlug || `${product.itemSlug}`.trim() === '') return
        const menuId = product.menuId

        const mainScrollContainer = document.getElementById('main-app-scroll-container')
        const productCatalogContext = {
            originPath: location.pathname,
            activeCategoryId,
            searchInput,
            searchKeyword,
            page,
            lastPage,
            totalProducts,
            products,
            storeSlug: activeStoreSlug,
            scrollTop: mainScrollContainer?.scrollTop || 0,
        }

        saveProductCatalogContext(productCatalogContext)
        setNavigatingToProduct(true)
        // Give React one paint so the navigation feedback is visible before
        // the detail route unmounts this page.
        window.setTimeout(() => {
            navigate(`/product/${encodeURIComponent(product.itemSlug)}`, {
                state: { productCatalogContext, productMenuId: menuId },
            })
        }, 120)
    }

    const handleLoadMore = () => {
        if (isBestSellerTab) return
        if (page >= lastPage || loadingMore) return
        setPage((currentPage) => currentPage + 1)
    }

    const handleSearchChange = (event) => {
        setSearchInput(event.target.value)
        setSuggestionsOpen(true)
    }

    const handleClearSearch = () => {
        setSearchInput('')
        setSearchKeyword('')
        setPage(1)
        setSuggestionsOpen(false)
    }

    const handleSuggestionClick = (product) => {
        setSuggestionsOpen(false)
        if (product?.itemSlug) {
            handleProductClick(product)
            return
        }

        setSearchInput(product?.itemName || '')
        setSearchKeyword(product?.itemName || '')
    }

    const handleStoreSelect = async (storeSlug) => {
        setStoreChanging(true)
        const result = await selectStore(storeSlug)
        setStoreChanging(false)
        if (!result?.status) {
            toast.error(result?.message || 'Gagal mengganti toko')
            return
        }
        setStoreModalOpen(false)
    }

    const LoadingSkeleton = () => (
        <div className="flex min-h-[70vh] flex-col items-center justify-center" role="status" aria-live="polite">
            <div className="h-11 w-11 animate-spin rounded-full border-4 border-gray-200 border-t-blue-600" />
            <p className="mt-4 text-sm font-medium text-gray-600">Memuat katalog produk...</p>
            <span className="sr-only">Mohon tunggu</span>
        </div>
    )

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
            <ToastContainer autoClose={1000} />
            {navigatingToProduct ? (
                <div className="fixed inset-0 z-[60] flex flex-col items-center justify-center bg-white/85 backdrop-blur-sm" role="status" aria-live="polite">
                    <div className="h-10 w-10 animate-spin rounded-full border-4 border-gray-200 border-t-blue-600" />
                    <p className="mt-3 text-sm font-medium text-gray-700">Memuat produk...</p>
                </div>
            ) : null}
            <div className={`mx-auto max-w-sm px-4 pt-2 ${Number(cartState.summary?.item_count) > 0 ? 'pb-40' : 'pb-24'}`}>
                <div className="scrollbar-hide">
                    <div className="mb-3 flex items-center justify-between gap-3">
                        <div className="min-w-0">
                            <div className="text-[11px] text-gray-500">Toko aktif</div>
                            <div className="truncate text-sm  text-gray-800">{storeLoading ? 'Memuat toko...' : (activeStore?.store_name || activeStore?.name || 'Toko belum tersedia')}</div>
                        </div>
                        <button type="button" onClick={() => setStoreModalOpen(true)} disabled={storeLoading || stores.length === 0} className="shrink-0 text-xs font-bold  hover:text-red-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:text-gray-400">Ganti Toko</button>
                    </div>
                    {promoBanners.length > 0 && (
                        <PromoBannerCarousel banners={promoBanners} onBannerClick={handlePromoClick} />
                    )}
                    <div ref={searchContainerRef} className="relative mb-3">
                        <div className="flex h-10 items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 shadow-sm focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-100">
                            <AiOutlineSearch className="shrink-0 text-lg text-gray-400" />
                            <input
                                type="search"
                                value={searchInput}
                                placeholder="Cari produk"
                                className="h-full min-w-0 flex-1 bg-transparent text-sm text-gray-800 outline-none placeholder:text-gray-400"
                                onChange={handleSearchChange}
                                onFocus={() => setSuggestionsOpen(true)}
                            />
                            {searchInput ? (
                                <button
                                    type="button"
                                    className="grid h-6 w-6 shrink-0 place-items-center rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-700"
                                    aria-label="Hapus pencarian"
                                    onClick={handleClearSearch}
                                >
                                    <AiOutlineClose className="text-sm" />
                                </button>
                            ) : null}
                        </div>

                        {showSuggestions ? (
                            <div className="absolute left-0 right-0 top-12 z-20 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-xl">
                                {waitingForSearch || (loading && searchActive) ? (
                                    <div className="px-3 py-3 text-xs text-gray-500">Mencari produk...</div>
                                ) : productSuggestions.length > 0 ? (
                                    <div className="max-h-80 overflow-y-auto py-1">
                                        {productSuggestions.map((product) => (
                                            <button
                                                key={product.menuId || product.itemSlug || product.itemName}
                                                type="button"
                                                className="flex w-full items-center gap-3 px-3 py-2 text-left hover:bg-gray-50"
                                                onClick={() => handleSuggestionClick(product)}
                                            >
                                                <img
                                                    src={product.imageUrl}
                                                    alt={product.itemName}
                                                    className="h-10 w-10 shrink-0 rounded-md object-cover"
                                                    onError={(event) => {
                                                        event.target.src = DefaultProduct
                                                    }}
                                                />
                                                <span className="min-w-0 flex-1">
                                                    <span className="block truncate text-xs font-semibold text-gray-800">{product.itemName}</span>
                                                    <span className="mt-0.5 block text-[11px] text-orange-600">
                                                        Rp {Intl.NumberFormat('en-ID').format(product.currentPrice)}
                                                    </span>
                                                </span>
                                                <span className={`shrink-0 text-[10px] font-medium ${product.isOutOfStock ? 'text-red-600' : 'text-gray-500'}`}>
                                                    {product.isOutOfStock ? 'Habis' : `Stok ${product.stock}`}
                                                </span>
                                            </button>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="px-3 py-3 text-xs text-gray-500">Produk tidak ditemukan</div>
                                )}
                            </div>
                        ) : null}
                    </div>

                    {storeModalOpen ? (
                        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" role="dialog" aria-modal="true" aria-label="Pilih toko">
                            <div className="max-h-[75vh] w-full max-w-sm overflow-y-auto rounded-2xl bg-white p-4">
                                <div className="mb-3 flex items-center justify-between"><h2 className="text-base font-bold text-gray-800">Pilih Toko</h2><button type="button" onClick={() => setStoreModalOpen(false)} className="p-1 text-gray-500"><AiOutlineClose /></button></div>
                                <div className="space-y-2">{stores.map((store) => { const slug = store?.store_slug || store?.slug; const selected = slug === activeStoreSlug; return <button key={slug} type="button" disabled={storeChanging} onClick={() => handleStoreSelect(slug)} className={`w-full rounded-lg border p-3 text-left text-sm ${selected ? 'border-blue-500 bg-blue-50 font-semibold text-white' : 'border-gray-200 text-gray-700'}`}>{store?.store_name || store?.name || slug}</button> })}</div>
                            </div>
                        </div>
                    ) : null}

                    {memberPoints > 0 && (
                        <div
                            className="mb-4 flex w-full items-center overflow-hidden rounded-xl text-left text-white shadow-lg"
                            style={{
                                background: `linear-gradient(135deg, ${primaryColor || '#BA4F4A'}, ${primaryColorHover || primaryColor || '#BA4F4A'})`,
                                '--tw-ring-color': primaryColor || '#BA4F4A',
                            }}
                        >
                            <div className="flex min-w-0 flex-1 items-center gap-3 px-4 py-3">
                                {/* <div className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-white/20">
                                    <FaCoins className="text-xl text-white" />
                                </div> */}
                                <div className="min-w-0">
                                    <div className="mb-1 text-[11px] font-medium text-white/80">Poin Anda</div>
                                    <div className="flex items-baseline gap-1.5">
                                        <span className="truncate font-bold leading-none">
                                            {Intl.NumberFormat('en-ID').format(memberPoints)}
                                        </span>
                                        <span className="text-sm font-medium text-white/90">poin</span>
                                    </div>
                                </div>
                            </div>
                            <button
                                type="button"
                                onClick={() => navigate('/voucher')}
                                className="m-2 flex shrink-0 items-center gap-2 rounded-lg bg-white/20 px-3 py-2 text-sm font-semibold text-white transition-colors hover:bg-white/30 focus:outline-none focus:ring-2 focus:ring-white/80"
                            >
                                <AiOutlineGift className="text-base" />
                                <span>Tukar Poin</span>
                                <span aria-hidden="true" className="text-lg leading-none">&rarr;</span>
                            </button>
                        </div>
                    )}

                    {categoryTabs.length > 0 && (
                        <div className="mb-2 -mx-4 px-4 overflow-x-auto scrollbar-hide">
                            <div className="inline-flex min-w-max gap-4 whitespace-nowrap pb-1">
                                {categoryTabs.map((tab) => {
                                    const isActive = activeCategoryId === tab.id

                                    return (
                                        <button
                                            key={tab.id}
                                            type="button"
                                            className={`product-category-tab shrink-0 border-b-2 px-0 py-2 text-[11px] transition-colors ${isActive ? 'font-bold' : 'border-transparent font-normal text-gray-700'}`}
                                            style={isActive ? { borderColor: primaryColor, color: primaryColor } : undefined}
                                            onClick={() => handleCategoryClick(tab.id)}
                                        >
                                            {tab.name}
                                        </button>
                                    )
                                })}
                            </div>
                        </div>
                    )}

                    <div className="mb-3 flex items-baseline justify-between gap-3">
                        <h2 className="text-base font-bold text-gray-800">{activeCategoryName}</h2>
                        <span className="shrink-0 text-xs font-medium text-gray-500">{Intl.NumberFormat('en-ID').format(totalProducts)} produk</span>
                    </div>

                    {loading ? (
                        <LoadingSkeleton />
                    ) : products.length === 0 ? (
                        <Card className="bg-gradient-to-r from-gray-50 to-white border-2 border-dashed border-gray-300">
                            <Card.Body className="text-center py-12">
                                <AiOutlineTag className="text-6xl text-gray-400 mx-auto mb-4" />
                                <p className="text-gray-500 font-medium text-lg">
                                    {searchActive ? 'Produk tidak ditemukan' : (storeError ? 'Toko belum tersedia' : (isBestSellerTab ? 'Belum ada produk terlaris' : 'Belum ada produk'))}
                                </p>
                                <p className="text-gray-400 text-sm mt-1">
                                    {searchActive ? 'Coba kata kunci lain atau ubah kategori' : (storeError || 'Produk akan segera ditambahkan')}
                                </p>
                            </Card.Body>
                        </Card>
                    ) : (
                        <div ref={productGridRef} className="relative">
                            <div
                                className={`space-y-3 transition-opacity duration-200 ease-out ${isPageChanging ? 'pointer-events-none opacity-40' : 'opacity-100'}`}
                                aria-busy={isPageChanging}
                            >
                                {products.map((product) => {
                                const canOpenDetail =
                                    product.itemSlug !== undefined &&
                                    product.itemSlug !== null &&
                                    `${product.itemSlug}`.trim() !== ''
                                const isLowStock =
                                    product.stock > 0 && product.stock <= LOW_STOCK_THRESHOLD
                                const cartItem = cartByMenuId.get(`${product.menuId}`)
                                const isCartBusy = Boolean(cartMutating[`${product.menuId}`])

                                return (
                                    <Card
                                        key={product.menuId}
                                        className={`w-full  border-b border-gray-300 overflow-hidden group $`}
                                        onClick={() => handleProductClick(product)}
                                    >
                                        <Card.Body className="grid grid-cols-[6rem_minmax(0,1fr)] items-start gap-x-3 gap-y-2 pl-0 pr-0 pb-3">
                                            <div className="relative row-span-2 h-24 w-24 overflow-hidden rounded-lg bg-gradient-to-br from-blue-50 to-purple-50">
                                                <img
                                                    className={`h-full w-full object-cover transition-all duration-300 ${product.isOutOfStock ? 'grayscale' : 'group-hover:scale-105'} ${canOpenDetail ? 'cursor-pointer' : ''}`}
                                                    src={product.imageUrl}
                                                    alt={product.itemName}
                                                    loading="lazy"
                                                    onError={(event) => {
                                                        event.target.src = DefaultProduct
                                                    }}
                                                    onClick={() => handleProductClick(product)}
                                                />
                                                {product.isOutOfStock && (
                                                    <div className="absolute left-1.5 top-1.5 rounded-full bg-red-500 px-1.5 py-0.5 text-[9px] font-bold text-white">
                                                        Stok habis
                                                    </div>
                                                )}
                                            </div>

                                            <div className="min-w-0 space-y-1">
                                                <h3
                                                    className="font-semibold text-gray-800 text-sm leading-snug"
                                                    style={{
                                                        display: '-webkit-box',
                                                        WebkitLineClamp: 2,
                                                        WebkitBoxOrient: 'vertical',
                                                        overflow: 'hidden',
                                                    }}
                                                >
                                                    {product.itemName}
                                                </h3>
                                                <div>
                                                    <p className="text-[13px] font-bold leading-none text-orange-600">
                                                        Rp {Intl.NumberFormat('en-ID').format(product.currentPrice)}
                                                    </p>
                                                    {product.hasMarketplaceDiscount && (
                                                        <div className="mt-1 flex items-center gap-1.5">
                                                            <span className="rounded bg-red-100 px-1 text-[9px] font-bold text-red-600">
                                                                PROMO
                                                            </span>
                                                            <span className="text-[10px] text-gray-400 line-through">
                                                                Rp {Intl.NumberFormat('en-ID').format(product.regularPrice)}
                                                            </span>
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="flex items-center gap-2 pt-1 text-[11px]">
                                                    <div className="inline-flex min-w-0 items-center gap-1 text-gray-500">
                                                        <AiFillStar className="shrink-0 text-[11px] text-amber-500" />
                                                        {product.reviewCount > 0 ? (
                                                            <>
                                                                <span className="font-medium text-gray-700">
                                                                    {product.rating.toFixed(1)}
                                                                </span>
                                                                <span className="truncate">({product.reviewCount} review)</span>
                                                            </>
                                                        ) : (
                                                            <span className="truncate">Belum ada review</span>
                                                        )}
                                                    </div>
                                                    <div
                                                        className={`font-medium ${
                                                            product.isOutOfStock
                                                                ? 'text-red-600'
                                                                : isLowStock
                                                                ? 'text-orange-600'
                                                                : 'text-gray-500'
                                                        }`}
                                                    >
                                                        {product.isOutOfStock
                                                            ? 'Stok habis'
                                                            : isLowStock
                                                            ? `Sisa ${product.stock}!`
                                                            : `Stok: ${product.stock}`}
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-start-2 flex min-w-0 justify-end" onClick={(event) => event.stopPropagation()}>
                                                {cartItem ? (
                                                    <div className="flex items-center gap-1">
                                                        <button type="button" aria-label="Kurangi jumlah" disabled={isCartBusy} onClick={() => mutateCart({ product, cart: cartItem, quantity: Math.max(0, Number(cartItem.quantity) - 1) })} className="grid h-8 w-8 place-items-center rounded-full border border-orange-300 text-orange-600 disabled:opacity-50"><AiOutlineMinus /></button>
                                                        <span className="min-w-[20px] text-center text-sm font-bold text-gray-800">{cartItem.quantity}</span>
                                                        <button type="button" aria-label="Tambah jumlah" disabled={isCartBusy || Number(cartItem.quantity) >= product.stock} onClick={() => mutateCart({ product, cart: cartItem, quantity: Number(cartItem.quantity) + 1 })} className="grid h-8 w-8 place-items-center rounded-full bg-orange-500 text-white disabled:opacity-50"><AiOutlinePlus /></button>
                                                        <button type="button" aria-label="Hapus dari keranjang" disabled={isCartBusy} onClick={() => mutateCart({ product, cart: cartItem, remove: true })} className="ml-1 grid h-8 w-8 place-items-center rounded-full text-red-500 disabled:opacity-50"><AiOutlineDelete /></button>
                                                    </div>
                                                ) : (
                                                    <button type="button" aria-label={`Tambah ${product.itemName} ke keranjang`} disabled={product.isOutOfStock || isCartBusy || cartLoading} onClick={() => mutateCart({ product })} className="grid h-9 w-9 place-items-center rounded-full bg-orange-500 text-xl font-medium leading-none text-white shadow-sm disabled:cursor-not-allowed disabled:bg-gray-300"><AiOutlinePlus /></button>
                                                )}
                                            </div>
                                        </Card.Body>
                                    </Card>
                                )
                                })}
                            </div>
                            {isPageChanging ? (
                                <div className="pointer-events-none absolute inset-0 grid place-items-center">
                                    <span className="rounded-full bg-slate-800/80 px-3 py-1.5 text-xs font-medium text-white shadow-sm">
                                        Memuat produk...
                                    </span>
                                </div>
                            ) : null}
                        </div>
                    )}

                    {!loading && !isBestSellerTab && products.length > 0 && page < lastPage ? (
                        <div className="mt-6">
                            <button
                                type="button"
                                className="btn w-full border-none bg-blue-600 text-sm font-semibold text-white hover:bg-blue-700"
                                onClick={handleLoadMore}
                                disabled={loadingMore}
                            >
                                {loadingMore ? 'Memuat...' : 'Tampilkan Lebih Banyak'}
                            </button>
                        </div>
                    ) : null}

                    {!loading && products.length > 0 && (isBestSellerTab || page >= lastPage) ? (
                        <p className="mt-6 pb-24 text-center text-xs text-gray-500">
                            {isBestSellerTab ? 'Semua produk terlaris sudah dimuat' : 'Semua produk sudah dimuat'}
                        </p>
                    ) : null}
                </div>
                {Number(cartState.summary?.item_count) > 0 ? (
                    <div className="fixed left-1/2 z-40 w-[calc(100%-1.5rem)] max-w-md -translate-x-1/2 rounded-2xl bg-slate-900 p-3 text-white shadow-xl" style={{ bottom: 'calc(4rem + max(0.75rem, env(safe-area-inset-bottom)))' }}>
                        <button type="button" onClick={handleCheckout} className="flex w-full items-center justify-between gap-3 text-left">
                            <span><span className="block text-xs text-white/70">{cartState.summary.item_count} item di keranjang</span><span className="text-base font-bold">Rp {Intl.NumberFormat('en-ID').format(Number(cartState.summary.subtotal) || 0)}</span></span>
                            <span className="rounded-xl bg-orange-500 px-4 py-2 text-sm font-bold">Checkout</span>
                        </button>
                    </div>
                ) : null}
            </div>
        </div>
    )
}

export default Product
